import React, { useEffect, useMemo, useRef, useState } from "react";
import reminderIcon from "../../assets/noun-reminder-6958823.svg";
import { useNavigate } from "react-router-dom";
import { useCurrentTasks } from "../../hooks/useCurrentTasks";
import { useActiveDossiers } from "../../hooks/useActiveDossiers";
import { useSeenNew } from "../../hooks/useSeenNew";
import tableStyles from "../DataTable/DataTable.module.css";

function formatDate(d: Date): string {
  try {
    return d.toLocaleDateString("nl-NL");
  } catch {
    return new Date().toLocaleDateString("nl-NL");
  }
}

export default function NotificationTray() {
  const [open, setOpen] = useState(false);
  const trayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: tasks = [] } = useCurrentTasks();
  const { data: dossiers = [] } = useActiveDossiers();
  const {
    isTaskNew,
    isDossierNew,
    markTaskSeen,
    markDossierSeen,
    getOrInitTaskFirstSeen,
    getOrInitDossierFirstSeen,
  } = useSeenNew();

  // Build notification list (unseen only)
  type TrayRow = {
    type: "task" | "dossier";
    id: string;
    link: string;
    datum: Date;
    soort: string;
    door: string;
    dossiernaam: string;
  };

  const notifications = useMemo<TrayRow[]>(() => {
    const taskNotifs = tasks
      .filter((t) => isTaskNew(t.id))
      .map((t) => {
        const datum = t.assignedAt
          ? new Date(t.assignedAt)
          : getOrInitTaskFirstSeen(t.id);
        return {
          type: "task" as const,
          id: t.id,
          link: `/dossier/${t.dossierKenmerk}`,
          datum,
          // Show only the type; header already says Taak/Wachten
          soort: t.taskType,
          door: t.assignedByName || "—",
          dossiernaam: t.dossierNaam,
        };
      });

    const dossierNotifs = dossiers
      .filter((d) => isDossierNew(d.kenmerk))
      .map((d) => {
        const datum = getOrInitDossierFirstSeen(d.kenmerk);
        return {
          type: "dossier" as const,
          id: d.kenmerk,
          link: `/dossier/${d.kenmerk}`,
          datum,
          soort: "Nieuw toegevoegd",
          door: "—",
          dossiernaam: d.naam,
        };
      });

    return [...taskNotifs, ...dossierNotifs].sort(
      (a, b) => b.datum.getTime() - a.datum.getTime(),
    );
  }, [
    tasks,
    dossiers,
    isTaskNew,
    isDossierNew,
    getOrInitTaskFirstSeen,
    getOrInitDossierFirstSeen,
  ]);

  const count = notifications.length;

  const onClickNotification = (n: TrayRow) => {
    if (n.type === "task") {
      markTaskSeen(n.id);
    } else {
      markDossierSeen(n.id);
    }
    setOpen(false);
    navigate(n.link);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (trayRef.current && !trayRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div
      ref={trayRef}
      style={{ position: "fixed", left: 10, bottom: 10, zIndex: 1000 }}
    >
      <button
        aria-label="Notificaties"
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          width: 44,
          height: 44,
          borderRadius: 9999,
          border: "1px solid #d1d5db",
          background: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={reminderIcon} alt="" width={24} height={24} aria-hidden />
        {count > 0 && (
          <span
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              background: "var(--btn-light-bg)",
              color: "var(--color-text)",
              borderRadius: 9999,
              fontSize: 12,
              minWidth: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 6px",
              border: "1px solid var(--color-border)",
            }}
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            left: 0,
            bottom: 56,
            width: 640,
            maxHeight: 420,
            overflowY: "auto",
            overflowX: "hidden",
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          <div
            style={{
              padding: 12,
              borderBottom: "1px solid #f3f4f6",
              fontWeight: 600,
            }}
          >
            Notificaties
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: 12, color: "#6b7280" }}>
              Geen nieuwe notificaties
            </div>
          ) : (
            <div
              className={tableStyles.tableWrapper}
              style={{ border: "none", boxShadow: "none", width: "100%" }}
            >
              <table
                className={tableStyles.table}
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Datum</th>
                    <th>Taak/Wachten</th>
                    <th style={{ width: 140 }}>Door</th>
                    <th style={{ width: 120 }}>Dossiernaam</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((n) => (
                    <tr
                      key={`${n.type}:${n.id}`}
                      className={tableStyles.clickableRow}
                      onClick={() => onClickNotification(n)}
                    >
                      <td
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {formatDate(n.datum)}
                      </td>
                      <td
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {n.soort}
                      </td>
                      <td
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {n.door}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{n.dossiernaam}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
